import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
  DISCOVERY_EVENT,
  FEATURED_SET_EVENT,
  PRIVATE_DISCOVERY_EVENT,
} from 'app/shared/utils/events';
import { useVisaAuth } from 'app/shared/utils/visa';
import { Event, EventAttendee } from 'app/shared/typings';
import { isEventInPast } from 'app/fan/utils/checkout';

export function useCheckoutRedirect(
  event?: Event | null,
  eventAttendee: EventAttendee | null = null
) {
  const { visaPreSaleEligible, canBypassVisaValidation } = useVisaAuth({
    skipLoadCardRedemptionsStatus: true,
  });
  const location = useLocation();

  const checkoutUrl = () => {
    if (!event) {
      return '';
    }

    const isDiscoveryEvent = event.type === DISCOVERY_EVENT;
    const isPrivateDiscoveryEvent = event.type === PRIVATE_DISCOVERY_EVENT;
    const isApplyFlowDiscoveryEvent =
      isDiscoveryEvent && event.attendeeFlow === 'apply';
    const isCompetition = event.competition && event.competition.id;

    if (isApplyFlowDiscoveryEvent || isPrivateDiscoveryEvent || isCompetition) {
      return `/events/${event.id}${location.search}`;
    }

    return `/checkout/${event.id}${location.search}`;
  };

  const shouldRedirectToEventPage = () => {
    if (!event) {
      return false;
    }

    const isDiscoveryEvent = event.type === DISCOVERY_EVENT;
    const isPrivateDiscoveryEvent = event.type === PRIVATE_DISCOVERY_EVENT;
    const isApplyFlowDiscoveryEvent =
      isDiscoveryEvent && event.attendeeFlow === 'apply';
    const isCompetition = event.competition && event.competition.id;

    const eventIsNotAvailable = !event.isAvailable;
    const eventIsSpecialEvent = event.isSpecialEvent;
    const eventHasNoRemainingSpaces = event.remainingSpaces < 1;
    const eventIsCancelled = !!event.cancelled;
    const eventIsNotPublished = !event.isPublished;
    const eventIsInThePast = isEventInPast(event.startsAt);
    const isFeaturedSetEvent = event.type === FEATURED_SET_EVENT;
    const eventIsFeaturedSetBeforeOnSaleAt =
      isFeaturedSetEvent && !!event.onSaleAt && !isEventInPast(event.onSaleAt);
    const userHasMaxTicketsForEvent =
      (eventAttendee?.tickets || []).length >= event.maxTicketsPerAttendee;

    return (
      isPrivateDiscoveryEvent ||
      isCompetition ||
      isApplyFlowDiscoveryEvent ||
      eventIsNotAvailable ||
      eventHasNoRemainingSpaces ||
      eventIsSpecialEvent ||
      eventIsCancelled ||
      eventIsNotPublished ||
      eventIsInThePast ||
      eventIsFeaturedSetBeforeOnSaleAt ||
      userHasMaxTicketsForEvent
    );
  };

  const shouldRedirectToVisaPartnershipPage = () => {
    return (
      !!event?.onPresale && !(visaPreSaleEligible || canBypassVisaValidation)
    );
  };

  const checkoutUrlForEvent = checkoutUrl();
  const shouldRedirectUserToEventPage = shouldRedirectToEventPage();
  const shouldRedirectUserToVisaPartnershipPage =
    shouldRedirectToVisaPartnershipPage();

  const values = useMemo(
    () => ({
      checkoutUrlForEvent,
      shouldRedirectUserToEventPage,
      shouldRedirectUserToVisaPartnershipPage,
    }),
    [
      checkoutUrlForEvent,
      shouldRedirectUserToEventPage,
      shouldRedirectUserToVisaPartnershipPage,
    ]
  );

  return values;
}
